<template>
	<div class="dashboard-shell">
		<transition name="banner-slide">
			<div v-if="successBanner" class="success-banner">{{ successBanner }}</div>
		</transition>
		
		<AppTopbar :user-name="userFullName" :logout-text="get_word('logout') || 'התנתקות'" />

		<main class="page-shell">
			<!-- Hero Banner -->
			<section class="hero-banner">
				<div class="hero-container">
					<div class="hero-content">
<!-- ספינר נקודות במרכז המסך -->
<div v-if="!dashData.user" class="fullscreen-loader">
  <div class="dot-spinner">
    <div class="dot1"></div>
    <div class="dot2"></div>
    <div class="dot3"></div>
  </div>
</div>

<!-- התוכן של העמוד -->
<h1 class="hero-title">
  שלום, {{ dashData.user ? dashData.user.first_name : '' }}
</h1>
						<p class="hero-subtitle" v-if="activePhone">מספר מנוי: {{ formatPhone(activePhone) }}</p>

						<!-- בחירת מספר במידה ויש כמה -->
						<div v-if="numbers.length > 1" class="number-selector">
							<span class="number-selector-title">בחר מנוי:</span>
							<div class="number-chips">
								<button
									v-for="(num, idx) in numbers"
									:key="num.id"
									type="button"
									class="number-chip"
									:class="{ active: selectedNumberIndex === idx }"
									@click="selectNumber(idx)"
								>
									{{ formatPhone(num.phone) || num.plan_name || ('מנוי ' + (idx + 1)) }}
								</button>
							</div>
						</div>
					</div>
					
					<!-- חותמת SVG מוטמעת ישירות בקוד -->
					<div class="hero-illustration">
						<svg class="rabbinical-stamp-svg" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
							<defs>
								<path id="stamp-arc-top" d="M 32,110 A 78,78 0 0,1 188,110" fill="none" />
								<path id="stamp-arc-bottom" d="M 188,110 A 78,78 0 0,1 32,110" fill="none" />
							</defs>

							<circle cx="110" cy="110" r="100" fill="none" stroke="currentColor" stroke-width="8" />
							<circle cx="110" cy="110" r="92" fill="none" stroke="currentColor" stroke-width="2" />

							<text class="stamp-arc-text">
								<textPath href="#stamp-arc-top" startOffset="50%" text-anchor="middle">
									ועדת הרבנים
								</textPath>
							</text>

							<path d="M 48,72 Q 110,50 172,72 Q 110,62 48,72 Z" fill="currentColor" />

							<g transform="translate(170, 110) scale(0.6)">
								<path d="M0,-25 C10,-15 15,-5 10,5 C5,15 -5,10 -10,0 C-15,-10 -5,-20 0,-25 Z" fill="currentColor" />
								<circle cx="2" cy="0" r="3" fill="currentColor" />
								<path d="M0,25 C10,15 15,5 10,-5 C5,-15 -5,-10 -10,0 C-15,10 -5,20 0,25 Z" fill="currentColor" />
							</g>
							<g transform="translate(50, 110) scale(-0.6, 0.6)">
								<path d="M0,-25 C10,-15 15,-5 10,5 C5,15 -5,10 -10,0 C-15,-10 -5,-20 0,-25 Z" fill="currentColor" />
								<circle cx="2" cy="0" r="3" fill="currentColor" />
								<path d="M0,25 C10,15 15,5 10,-5 C5,-15 -5,-10 -10,0 C-15,10 -5,20 0,25 Z" fill="currentColor" />
							</g>

							<text x="110" y="124" text-anchor="middle" class="stamp-main-bold">
								מאושר
							</text>

							<text class="stamp-arc-text">
								<textPath href="#stamp-arc-bottom" startOffset="50%" text-anchor="middle">
									לענייני תקשורת
								</textPath>
							</text>
						</svg>
					</div>
				</div>
			</section>

			<div class="main-container">
				<!-- כרטיסיית מידע מרכזית -->
				<section class="floating-summary-card">
					<div class="summary-main-info">
						<div class="summary-col">
							<span class="summary-label">מסלול נוכחי</span>
							<strong class="summary-value">{{ activePlan || 'לא הוגדר עדיין' }}</strong>
						</div>
						<div class="summary-col border-right">
							<span class="summary-label">מצב רשת</span>
							<strong class="summary-value">{{ currentAntenna ? currentAntenna.text : 'לא שונתה' }}</strong>
						</div>
						<div class="summary-col border-right">
							<span class="summary-label">סטטוס חובות</span>
							<strong class="summary-value" :class="{ 'text-danger': dashData.has_debt }">
								{{ dashData.has_debt ? formatAmount(dashData.total_debt) + ' ₪ לתשלום' : 'אין חובות' }}
							</strong>
						</div>
						<div class="summary-col border-right">
							<span class="summary-label">אמצעי תשלום</span>
							<strong class="summary-value" v-if="dashData.payment_four_digits === 'saved'">כרטיס שמור</strong>
							<strong class="summary-value" v-else-if="dashData.payment_four_digits">מסתיים ב-{{ dashData.payment_four_digits }}</strong>
							<strong class="summary-value muted" v-else>לא נשמר</strong>
						</div>
					</div>
					
					<div class="summary-secondary-info" v-if="lastAddedPackage">
						<span class="tag-info">חבילה אחרונה שנוספה: {{ lastAddedPackage.name }}</span>
					</div>
				</section>

				<!-- פעולות בשירות עצמי -->
				<section class="actions-section">
					<h2 class="section-title">פעולות בשירות עצמי</h2>

					<div class="dashboard-actions-grid">
						<router-link to="/change-plan" class="action-card-link">
							<article class="action-card">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
								</div>
								<h3 class="action-title">שינוי מסלול</h3>
							</article>
						</router-link>

						<router-link to="/change-network" class="action-card-link">
							<article class="action-card">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
								</div>
								<h3 class="action-title">שינוי רשת</h3>
							</article>
						</router-link>

						<router-link to="/change-packages" class="action-card-link">
							<article class="action-card">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
								</div>
								<h3 class="action-title">הוספת חבילת דקות</h3>
							</article>
						</router-link>

						<router-link to="/usage" class="action-card-link">
							<article class="action-card">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
								</div>
								<h3 class="action-title">שימוש בחבילה</h3>
							</article>
						</router-link>

						<router-link to="/payment-method" class="action-card-link">
							<article class="action-card">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
								</div>
								<h3 class="action-title">אמצעי תשלום</h3>
							</article>
						</router-link>

						<router-link to="/debts" class="action-card-link">
							<article class="action-card" :class="{ 'card-danger': dashData.has_debt }">
								<div class="icon-circle">
									<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
								</div>
								<h3 class="action-title">פירוט חובות</h3>
							</article>
						</router-link>
					</div>
				</section>
			</div>
		</main>
		<AppFooter />
	</div>
</template>
<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

export default {
	name: 'Home',
	components: { AppTopbar, AppFooter },
	data() {
		const antenna = (() => { try { return JSON.parse(localStorage.getItem('current_antenna') || 'null') } catch { return null } })()
		const pkg     = (() => { try { return JSON.parse(localStorage.getItem('last_added_package') || 'null') } catch { return null } })()
		return {
			loadingDash: true,
			dashData: { user: null, plan: null, numbers: [], payment_four_digits: null, has_debt: false, total_debt: 0 },
			selectedNumberIndex: parseInt(localStorage.getItem('selected_number_index') || '0', 10),
			currentAntenna: antenna,
			lastAddedPackage: pkg && (Date.now() - pkg.time < 7 * 24 * 60 * 60 * 1000) ? pkg : null,
			successBanner: '',
			_bannerTimer: null,
		}
	},
	computed: {
		userFullName() {
			if (!this.dashData.user) return ''
			return `${this.dashData.user.first_name || ''} ${this.dashData.user.last_name || ''}`.trim()
		},
		numbers() {
			return this.dashData.numbers || []
		},
		activeNumber() {
			if (!this.numbers.length) return null
			const idx = Math.min(this.selectedNumberIndex, this.numbers.length - 1)
			return this.numbers[idx] || null
		},
		activePhone() {
			if (this.activeNumber && this.activeNumber.phone) return this.activeNumber.phone
			if (this.selectedNumberIndex === 0 && this.dashData.user) return this.dashData.user.phone
			return ''
		},
		activePlan() {
			if (this.activeNumber && this.activeNumber.plan_name) return this.activeNumber.plan_name
			return this.dashData.plan ? this.dashData.plan.name : ''
		},
	},
	mounted() {
		if (this.isNull(localStorage.getItem('user_token'))) {
			this.$router.push('/login')
			return
		}
		const msg = this.$route?.query?.success
		if (msg) {
			this.successBanner = decodeURIComponent(msg)
			this._bannerTimer = setTimeout(() => { this.successBanner = '' }, 5000)
			this.$router.replace({ path: '/' })
		}
		this.loadDashboard()
	},
	beforeUnmount() {
		if (this._bannerTimer) clearTimeout(this._bannerTimer)
	},
	methods: {
		loadDashboard() {
			this.loadingDash = true
			this.api({ action: 'get_my_dashboard_data', method: 'get' }, (response) => {
				if (response?.data?.success) {
					this.dashData = response.data
					if (this.dashData.user) {
						this.$store.commit('setUser', { user: this.dashData.user })
					}
					if (this.numbers.length > 0 && this.selectedNumberIndex >= this.numbers.length) {
						this.selectedNumberIndex = 0
						localStorage.setItem('selected_number_index', '0')
					}
				}
				this.loadingDash = false
			}, () => { this.loadingDash = false })
		},
		selectNumber(idx) {
			this.selectedNumberIndex = idx
			localStorage.setItem('selected_number_index', String(idx))
		},
		formatPhone(phone) {
			if (!phone) return ''
			const p = String(phone).replace(/\D/g, '')
			if (p.startsWith('972') && p.length === 12) return '0' + p.slice(3)
			return phone
		},
		formatAmount(val) {
			return this.format_number((Math.round(Number(val) * 100) / 100).toString())
		},
	},
}
</script>


<style lang="scss" scoped src="@/styles/pages/home.scss"></style>
